import type { AudioEnvelope } from '../audio/envelope';
import type { LoudnessResult } from '../audio/loudness';
import type { VoiceTrack } from '../audio/voice';
import { TIME_EPSILON } from '../time/ranges';
import type { AssetId, SequenceLayout } from './sequence';

/**
 * Lays per-video analyses end to end the way the clips play, so the waveform,
 * pause and hesitation finding work on the whole edit exactly as they do on
 * one video.
 */
export interface SoundAnalysis {
  readonly envelope: AudioEnvelope;
  readonly loudness: LoudnessResult;
  readonly voice: VoiceTrack;
}

/** Copies one per-window series onto the row; stretches with no analysis (yet) stay at 0. */
function placeSeries(layout: SequenceLayout, windowSec: number, seriesOf: (assetId: AssetId) => { readonly windowSec: number; readonly values: Float32Array } | null): Float32Array {
  const out = new Float32Array(Math.ceil(layout.duration / windowSec - TIME_EPSILON));
  for (const clip of layout.clips) {
    const series = seriesOf(clip.assetId);
    if (!series) continue;
    const from = Math.round(clip.start / windowSec);
    const to = Math.min(out.length, Math.round(clip.end / windowSec));
    for (let k = from; k < to; k++) {
      const assetTime = clip.in + (k * windowSec - clip.start);
      out[k] = series.values[Math.floor(assetTime / series.windowSec + TIME_EPSILON)] ?? 0;
    }
  }
  return out;
}

/**
 * Loudness of clips played one after the other: their loudness averaged by
 * energy over how long each plays. Close to a measurement of the joined
 * sound, and only shown; each clip's own sound is evened out separately.
 */
export function combineLoudness(parts: ReadonlyArray<{ readonly loudness: LoudnessResult; readonly seconds: number }>): LoudnessResult {
  let energy = 0;
  let seconds = 0;
  let peak = 0;
  for (const part of parts) {
    peak = Math.max(peak, part.loudness.peak);
    if (part.loudness.integratedLufs === null || !(part.seconds > 0)) continue;
    energy += part.seconds * 10 ** (part.loudness.integratedLufs / 10);
    seconds += part.seconds;
  }
  return { integratedLufs: seconds > 0 ? 10 * Math.log10(energy / seconds) : null, peak };
}

/**
 * The sound analysis of the edit, or null while no clip's analysis is in.
 * A single clip showing a whole video returns that video's analysis as it is.
 */
export function placeSound(layout: SequenceLayout, analysisOf: (assetId: AssetId) => SoundAnalysis | null | undefined): SoundAnalysis | null {
  const present = layout.clips.map((clip) => analysisOf(clip.assetId)).filter((analysis): analysis is SoundAnalysis => Boolean(analysis));
  const first = present[0];
  if (!first) return null;
  const only = layout.clips[0];
  if (layout.clips.length === 1 && only && only.in <= TIME_EPSILON && Math.abs(only.end - layout.duration) < TIME_EPSILON) {
    const whole = analysisOf(only.assetId);
    if (whole && only.out >= whole.envelope.rms.length * whole.envelope.windowSec - whole.envelope.windowSec) return whole;
  }
  const windowSec = first.envelope.windowSec;
  const envelope = (id: AssetId) => {
    const analysis = analysisOf(id);
    return analysis ? { windowSec: analysis.envelope.windowSec, values: analysis.envelope.rms } : null;
  };
  const pitch = (id: AssetId) => {
    const analysis = analysisOf(id);
    return analysis ? { windowSec: analysis.voice.windowSec, values: analysis.voice.pitch } : null;
  };
  const change = (id: AssetId) => {
    const analysis = analysisOf(id);
    return analysis ? { windowSec: analysis.voice.windowSec, values: analysis.voice.change } : null;
  };
  const voiceWindow = first.voice.windowSec;
  return {
    envelope: { windowSec, rms: placeSeries(layout, windowSec, envelope) },
    voice: { windowSec: voiceWindow, pitch: placeSeries(layout, voiceWindow, pitch), change: placeSeries(layout, voiceWindow, change) },
    loudness: combineLoudness(layout.clips.flatMap((clip) => {
      const analysis = analysisOf(clip.assetId);
      return analysis ? [{ loudness: analysis.loudness, seconds: clip.end - clip.start }] : [];
    })),
  };
}

/**
 * Small frames of each video placed where their clips play (for the
 * timeline). A clip too short to hold a frame of its own borrows the nearest.
 */
export function placeFrames<T extends { readonly time: number }>(layout: SequenceLayout, framesOf: (assetId: AssetId) => readonly T[]): T[] {
  const placed: T[] = [];
  for (const clip of layout.clips) {
    const frames = framesOf(clip.assetId);
    if (frames.length === 0) continue;
    const inside = frames.filter((frame) => frame.time >= clip.in - TIME_EPSILON && frame.time < clip.out);
    const middle = (clip.in + clip.out) / 2;
    if (inside.length === 0) {
      placed.push({ ...frames.reduce((best, frame) => (Math.abs(frame.time - middle) < Math.abs(best.time - middle) ? frame : best)), time: clip.start });
      continue;
    }
    for (const frame of inside) placed.push({ ...frame, time: clip.start + Math.max(0, frame.time - clip.in) });
  }
  return placed.sort((a, b) => a.time - b.time);
}

/**
 * Timed items of one asset (words, in its own seconds) where its clips show
 * them on the row; an item belongs to the clip its start falls in.
 */
export function placeTimed<T extends { readonly start: number; readonly end: number }>(layout: SequenceLayout, assetId: AssetId, items: readonly T[]): T[] {
  const placed: T[] = [];
  for (const clip of layout.clips) {
    if (clip.assetId !== assetId) continue;
    for (const item of items) {
      if (item.start < clip.in - TIME_EPSILON || item.start >= clip.out) continue;
      const start = clip.start + Math.max(0, item.start - clip.in);
      placed.push({ ...item, start, end: Math.min(clip.end, start + (item.end - item.start)) });
    }
  }
  return placed.sort((a, b) => a.start - b.start);
}
